<template>
	<div class="is-size-6" :class="determineClasses()">
		<div class="cart-item-options" :class="highlightSibling('0')">
			<BaseIcon v-if="isDivisible()"
				:fill="false"
				:isCustom="true"
				:icon="$appConstants.items.choices.icons.divisionWhole"
				class="divisible-icon"
				size="2x"
				:class="styleClassWholeOption()" />
            <div class="listed-choices" @click="listChoiceClickHandler" v-html="listChoices('0', this.options)"></div>
        </div>
        <div class="cart-item-options" :class="highlightSibling('1')">
			<BaseIcon v-if="!isMinified"
				:fill="false"
				:isCustom="true"
				:icon="$appConstants.items.choices.icons.divisionLeft"
				size="2x"
				class="divisible-icon"/>
            <p class="item-side" v-else>Left: </p>
            <div class="listed-choices" @click="listChoiceClickHandler" v-html="listChoices('1', this.options)"></div>
        </div>
        <div class="cart-item-options" :class="highlightSibling('2')">
            <BaseIcon v-if="!isMinified"
				:fill="false"
				:isCustom="true"
				:icon="$appConstants.items.choices.icons.divisionRight"
				size="2x"
				class="divisible-icon"/>
            <p class="item-side" v-else>Right: </p>
			<div class="listed-choices" @click="listChoiceClickHandler" v-html="listChoices('2', this.options)"></div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { CART, CART_OPTION, MENU_OPTIONS } from '@/stores/defs';
import { flatten, map, find, includes, isFunction } from 'lodash';
import Formatter from '@/utils/Formatter';
import { determineSiblingUsed } from '@/utils/helper.functions';

import choiceActionMixin from '@Mixin/choiceActionMixin';
import menuImageMixin from '@Mixin/menuImageMixin';

export default {
	mixins: [
		choiceActionMixin,
		menuImageMixin,
	],
	props: {
		options: {
			type: Array,
		},
		isMinified: {
			type: Boolean,
			default: false,
		},
		isRecentOrder: {
			type: Boolean,
			default: false,
		},
		reveal: {
			type: Boolean,
			default: false,
		},
	},
	computed: {
		...mapGetters({
			cartItem: CART.GET_CURRENT_ITEM,
			listedChoices: CART_OPTION.LIST_ALL,
			siblingOptionChoices: CART_OPTION.SIBLING_OPTION_CHOICES,
			choicesForOption: MENU_OPTIONS.GET_CHOICES,
		}),
		isDetail() {
			const route = this.$route;
			if (route.name === 'item-detail' || route.name === 'checkout') {
				return true;
			}
			return false;
		},
	},
	methods: {
		hasSiblings() {
			for (const option of this.options) {
				const flag = option.hasSiblings();
				if (flag) {
					return true;
				}
			}
			return false;
		},
		isDivisible() {
			for (const option of this.options) {
				const flag = option.isDivisible;
				if (flag) {
					return true;
				}
			}
			return false;
		},
		styleClassPrice(displayPrice) {
			if (displayPrice.indexOf('-') !== -1) {
				return `<div class="choice-price"><span>($${displayPrice})</span></div>`;
			}
			return `<div class="choice-price"><span>(+$${displayPrice})</span></div>`;
		},
		highestPricedSibling() {
			// TODO place this logic into presenter style vue
			const cartItem = this.cartItem;
			const options = this.options;
			let siblingUsed = '1';
			if (isFunction(cartItem.getTotalsFlyweight)) {
				const totals = cartItem.getTotalsFlyweight(options);
				siblingUsed = determineSiblingUsed(totals);
			}
			return siblingUsed;
		},
		shouldDisplayPrice(choice) {
			const hasPrice = (choice.price !== 0);
			const notIncluded = !choice.included;
			const notFree = !choice.free;
			return hasPrice && notIncluded && notFree;
		},
		listChoices(siblingId, options) {
			const detail = this.isDetail;
			const highestPricedSibling = this.highestPricedSibling();
			const displayForSibling = (siblingId === highestPricedSibling || siblingId === '0');
			const listedChoices = this.listedChoices(siblingId, options);
			const excludedDisplayOptions = [];
			options.forEach((option) => {
				const optionName = option.name.toLowerCase();
				if (optionName.indexOf('size') !== -1) {
					excludedDisplayOptions.push(option.id);
				}
			});
			const flattenedChoices = flatten(listedChoices);
			const displayChoices = map(flattenedChoices, (choice) => {
				let displayChoice = `<div class="choice-name truncate">${choice.name}</div>`;
				let shouldDisplayPrice = this.shouldDisplayPrice(choice);
				if (excludedDisplayOptions.includes(choice.parentId)) {
					shouldDisplayPrice = false;
				}
				if (shouldDisplayPrice && displayForSibling) {
					const displayPrice = Formatter.centsToDollars(choice.price);
					displayChoice += this.styleClassPrice(displayPrice);
				}
				if (detail) {
					const menuChoice = this.getChoiceFromMenu(choice.parentId, choice.id);
					if (!menuChoice) {
						return '';
					}
					return this.buildChoiceDetail(menuChoice, displayChoice, choice.quantity);
				}
				return displayChoice;
			});
			const joiningString = (detail) ? '<br />' : ', ';
			return displayChoices.join(joiningString);
		},
		buildChoiceDetail(menuChoice, displayChoice, quantity = 1) {
			const imageElement = this.getMenuImageHTML(menuChoice);
			const hasImage = (imageElement !== '');
			const isCrossedOut = (quantity === 0 && hasImage) ? 'crossed-out' : '';
			const removeIcon = (menuChoice.required) ? '' : this.getRemoveIconGeneratedHTML(menuChoice);
			const choiceDetailHTML = `
					<div class="choice-detail animate-in-list ${isCrossedOut}">
						${imageElement} 
						${displayChoice} 
						${removeIcon}
					</div>
				`;
			return choiceDetailHTML.trim();
		},
		getRemoveIconGeneratedHTML(choice) {
			const iconHTML = `
					<svg
						aria-hidden="true"
						data-prefix="fal"
						data-icon="times-circle"
						data-option-id="${choice.parentId}"
						data-choice-id="${choice.id}"
						role="img"
						xmlns="http://www.w3.org/2000/svg"
						viewBox="0 0 512 512"
						class="svg-inline--fa fa-times-circle fa-w-16 fa-2x choice-detail-close-icon">
						<path
							fill="currentColor"
							d="M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zm0 464c-118.7 0-216-96.1-216-216 0-118.7 96.1-216 216-216 118.7 0 216 96.1 216 216 0 118.7-96.1 216-216 216zm94.8-285.3L281.5 256l69.3 69.3c4.7 4.7 4.7 12.3 0 17l-8.5 8.5c-4.7 4.7-12.3 4.7-17 0L256 281.5l-69.3 69.3c-4.7 4.7-12.3 4.7-17 0l-8.5-8.5c-4.7-4.7-4.7-12.3 0-17l69.3-69.3-69.3-69.3c-4.7-4.7-4.7-12.3 0-17l8.5-8.5c4.7-4.7 12.3-4.7 17 0l69.3 69.3 69.3-69.3c4.7-4.7 12.3-4.7 17 0l8.5 8.5c4.6 4.7 4.6 12.3 0 17z"
							class="">
						</path>
					</svg>
				`;
			return iconHTML.trim();
		},
		getChoiceFromMenu(optionId, choiceId) {
			const choices = this.choicesForOption(optionId);
			const choice = find(choices, (_choice) => {
				return choiceId === _choice.id;
			});
			return choice;
		},
		listChoiceClickHandler(event) {
			const target = event.target;
			const classes = target.classList;
			if (includes(classes, 'choice-detail-close-icon')) {
				const optionId = target.dataset['optionId'];
				const choiceId = target.dataset['choiceId'];
				const choice = this.getChoiceFromMenu(optionId, choiceId);
				const quantity = choice.includedQuantity;
				// TODO do not assume siblingId, items may have default sides (maybe)
				this.setChoiceDivision({ choice, siblingId: '0' });
				this.choiceAction({ choice, quantity });
			}
		},
		highlightSibling(siblingId) {
			let siblingChoices = this.siblingOptionChoices(siblingId, this.options);
			siblingChoices = _.filter(siblingChoices, (choice) => {
				return choice.quantity > 0;
			});
			if (siblingChoices.length > 0) {
				return 'selected';
			}
			return 'hidden';
		},
		revealStateClass() {
			return (this.reveal) ? 'reveal' : '';
		},
		minifiedStateClass() {
			return (!this.isMinified) ? 'item-detail-info-option' : 'item-detail-info-option-minified';
		},
		determineClasses() {
			return `${this.minifiedStateClass()} ${this.revealStateClass()}`;
		},
		styleClassWholeOption() {
			return '';
		},
	},
};
</script>

<style lang="scss" scoped>
	@import 'globalStyles';

	.item-detail-info-option {
		margin: 0;
	}
	.checkout-item {
		.item-detail-info-option {
			margin: 1rem 0;
		}
	}

	.item-detail-info-option-minified {
		margin-left: 1em;
	}

	.item-detail-info-option-minified {
		.cart-item-options {
			font-size: 0.9rem;
			width: 60%;

			color: $storeOptionTextColor;

			.divisible-icon {
				margin-right: 0.5rem;
				font-size: 0.5rem;
			}
		}
	}
	.checkout-item {
		.item-detail-info-option-minified {
			margin-left: 0;
		}
	}

	.cart-item-options {
		display: flex;

		margin: 0.5rem 0;
		.listed-choices {
			white-space: pre-wrap;
			text-overflow: ellipsis;
			line-height: 1.3em;
			width: inherit;
		}
	}

    .item-side {
        padding-right: 0.2em;
	}

	.divisible-icon {
		margin-right: 1rem;
	}

	.recent-order-item-info-container {
		.cart-item-options {
			margin: 0.5rem 0.5rem 0.5rem 0;

			.divisible-icon {
				margin-right: 0.5rem;
				font-size: 0.8rem;
			}
		}
	}

	@include desktop {
		.cart-item-options {
			.listed-choices {
				margin: 0.4em 0;
			}
		}
	}

    @include mobile {
        .item-detail-info-option {
            padding: 0;
            margin-bottom: 0;


			height: 100%;
            width: 100%;

            transition: 0.4s all;

            &.reveal {
                margin-bottom: 25vh;

                height: auto;

                overflow: scroll;
			}
			.divisible-icon {
				margin-top: 0.4em;
				margin-left: 0.8em;
				margin-right: 0.8em;
				font-size: 0.7em;
			}
        }
        .recent-order-item-info-container {
			.cart-item-options {
				margin: 0 1rem;
				.listed-choices {
					margin-bottom: 0;
					margin-top: 0.4em;
					overflow-x: hidden;
				}
			}
            .item-detail-info-option {
                height: inherit;
			}
		}
        .checkout-item {
            .item-detail-info-option {
                margin-bottom: 1rem;

                height: auto;
            }
		}
		.detail--tablet-compact {
			.item-detail-info-option {
				display: flex;
				flex-direction: column;
				flex-wrap: wrap;
			}
			.cart-item-options {
				width: 40vw;
			}
		}
	}
	@include phone {
		.recent-order-item-info-container {
			.cart-item-options {
				.listed-choices {
					width: 40vw;
				}
			}
		}
	}
</style>



// WEBPACK FOOTER //
// src/components/menu/CartItemInfoSiblings.vue